import { memo } from 'react'
import LocationPickerMap from '../../../components/sighting/LocationPickerMap'
import { TruckIcon, XMarkIcon, PhotoIcon, MagnifyingGlassIcon, InformationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { baseInputClasses, labelClasses, getTextareaClasses } from '../../../styles/inputStyles'

/**
 * SightingForm - Form for creating new sightings
 * 
 * @param {Object} formData - Form state data
 * @param {Function} onChange - Form data change handler
 * @param {Function} onSubmit - Form submission handler
 * @param {string} entitySearch - Entity search input
 * @param {Function} onEntitySearchChange - Entity search change handler
 * @param {Array} entitySearchResults - Entity search results
 * @param {boolean} showEntitySearch - Show entity search dropdown
 * @param {Function} onShowEntitySearchChange - Toggle entity search dropdown
 * @param {Object} selectedEntity - Selected entity object
 * @param {Function} onSelectEntity - Entity selection handler
 * @param {Function} onClearEntity - Clear selected entity
 * @param {Object} mapPosition - Map marker position
 * @param {Function} onMapPositionChange - Map position change handler
 * @param {string} imagePreview - Image preview URL
 * @param {Function} onImageSelect - Image file selection handler
 * @param {Function} onRemoveImage - Remove image handler
 * @param {boolean} loading - Loading state
 * @param {boolean} uploadingImage - Image upload in progress
 * 
 * @idempotent - Pure form component with React.memo
 * @atomic - Single responsibility: sighting creation form (<400 lines)
 */
const SightingForm = memo(function SightingForm({
  formData,
  onChange,
  onSubmit,
  entitySearch,
  onEntitySearchChange,
  entitySearchResults,
  showEntitySearch,
  onShowEntitySearchChange,
  selectedEntity,
  onSelectEntity,
  onClearEntity,
  mapPosition,
  onMapPositionChange,
  imagePreview,
  onImageSelect,
  onRemoveImage,
  loading,
  uploadingImage
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Entity Selection */}
      <div>
        <label className={labelClasses}>
          Entity <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={entitySearch}
                onChange={(e) => onEntitySearchChange(e.target.value)}
                onFocus={() => onShowEntitySearchChange(true)}
                placeholder="Search for an entity..."
                className={`${baseInputClasses} pl-10`}
                disabled={loading}
              />
            </div>
            {selectedEntity && (
              <button
                type="button"
                onClick={onClearEntity}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                title="Clear selection"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showEntitySearch && entitySearchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {entitySearchResults.map((entity) => (
                <button
                  key={entity.id}
                  type="button"
                  onClick={() => onSelectEntity(entity)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <div className="font-medium text-gray-900">{entity.name}</div>
                  <div className="text-sm text-gray-500 capitalize">
                    {entity.type} • {entity.category}
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedEntity && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <TruckIcon className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-medium text-blue-900">{selectedEntity.name}</div>
                  <div className="text-sm text-blue-700 capitalize">
                    {selectedEntity.type} • {selectedEntity.category}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {!selectedEntity && (
          <p className="mt-1 text-xs text-gray-500">
            Start typing to search for an entity
          </p>
        )}
      </div>

      {/* Location Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Location <span className="text-red-500">*</span>
        </label>
        <p className="text-sm text-gray-500 mb-3">
          Click on the map to select the sighting location
        </p>
        
        <LocationPickerMap
          userLocation={
            mapPosition 
              ? [mapPosition.lat, mapPosition.lng]
              : formData.latitude && formData.longitude
              ? [formData.latitude, formData.longitude]
              : [39.7392, -104.9903] // Denver as default
          }
          position={mapPosition}
          setPosition={onMapPositionChange}
          height="400px"
          zoom={13}
        />
        <p className="mt-2 text-xs text-gray-500">
          <InformationCircleIcon className="h-4 w-4 inline mr-1" />
          Click anywhere on the map to set the location. The coordinates will be updated automatically.
        </p>

        {formData.latitude !== null && formData.longitude !== null && (
          <div className="mt-3 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Latitude
                {mapPosition && (
                  <span className="ml-1 text-xs text-green-600 inline-flex items-center gap-1">
                    <CheckCircleIcon className="h-3 w-3" />
                    Set via map
                  </span>
                )}
              </label>
              <input
                type="number"
                step="any"
                value={formData.latitude || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseFloat(e.target.value) : null
                  onChange({ ...formData, latitude: value })
                  if (value && formData.longitude) {
                    onMapPositionChange({ lat: value, lng: formData.longitude })
                  }
                }}
                className={baseInputClasses}
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Longitude
                {mapPosition && (
                  <span className="ml-1 text-xs text-green-600 flex items-center gap-1">
                    <CheckCircleIcon className="h-3 w-3" />
                    Set via map
                  </span>
                )}
              </label>
              <input
                type="number"
                step="any"
                value={formData.longitude || ''}
                onChange={(e) => {
                  const value = e.target.value ? parseFloat(e.target.value) : null
                  onChange({ ...formData, longitude: value })
                  if (value && formData.latitude) {
                    onMapPositionChange({ lat: formData.latitude, lng: value })
                  }
                }}
                className={baseInputClasses}
                disabled={loading}
              />
            </div>
          </div>
        )}
      </div>

      {/* Address */}
      <div>
        <label className={labelClasses}>
          Address
        </label>
        <input
          type="text"
          value={formData.address}
          onChange={(e) => onChange({ ...formData, address: e.target.value })}
          placeholder="Auto-populated from map or enter manually"
          className={baseInputClasses}
          disabled={loading}
        />
      </div>

      {/* Notes */}
      <div>
        <label className={labelClasses}>
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => onChange({ ...formData, notes: e.target.value })}
          placeholder="Optional notes about this sighting..."
          rows={4}
          className={getTextareaClasses()}
          disabled={loading}
        />
      </div>

      {/* Image Upload */}
      <div>
        <label className={labelClasses}>
          Image (Optional)
        </label>
        
        {!imagePreview ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
            <input
              type="file"
              id="image-upload"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              onChange={onImageSelect}
              className="hidden"
              disabled={loading || uploadingImage}
            />
            <label
              htmlFor="image-upload"
              className="cursor-pointer"
            >
              <PhotoIcon className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-1">
                {uploadingImage ? 'Uploading...' : 'Click to upload an image'}
              </p>
              <p className="text-xs text-gray-500">
                JPEG, PNG, GIF, or WebP (max 10MB)
              </p>
            </label>
          </div>
        ) : (
          <div className="relative">
            <img
              src={imagePreview}
              alt="Sighting preview"
              className="w-full h-64 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={onRemoveImage}
              className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
              disabled={loading}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex gap-4 pt-4 border-t">
        <button
          type="submit"
          disabled={loading || !formData.entityId || formData.latitude === null || formData.longitude === null}
          className="flex-1 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
        >
          {loading ? 'Creating...' : 'Create Sighting'}
        </button>
        <button
          type="button"
          onClick={() => window.history.back()}
          disabled={loading}
          className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 transition-colors font-medium"
        >
          Cancel
        </button>
      </div>
    </form>
  )
})

export default SightingForm
